import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Tippy from '@tippyjs/react';
import SimpleHeaderPills from './SimpleHeaderPills';
import SimpleEventDetailsModal from './SimpleEventDetailsModal';
import EventTemplateBar from '../EventTemplateBar';
import { statePillFor } from '../../utils/offerHeaderHelpers';
import { canCreateConfirm } from '../../../../shared/offerConfirmEligibility';
import { offsetDateForServer, toCalendarDate } from '../../../../shared/timeZoneLogic';
import '../../styles/simpleOfferLayout.css';

const iconBtnStyle = {
  borderRadius: '8px',
  width: '36px',
  height: '36px',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--so-surface)',
  border: '1px solid var(--so-border)',
  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
  color: 'var(--so-text-2)',
  cursor: 'pointer',
};

const dangerIconBtnStyle = {
  ...iconBtnStyle,
  border: '1px solid var(--so-danger-border)',
  color: 'var(--so-danger)',
};

const disabledIconBtnStyle = {
  ...iconBtnStyle,
  opacity: 0.5,
  cursor: 'not-allowed',
};

const primaryBtnStyle = {
  fontWeight: 600,
  borderRadius: '8px',
  borderWidth: '1px',
  paddingTop: '5.6px',
  paddingBottom: '5.6px',
  fontSize: '14px',
  lineHeight: '21px',
  boxShadow:
    '0px 0px 0px 1px rgba(10, 13, 18, 0.18) inset, '
    + '0px -2px 0px 0px rgba(10, 13, 18, 0.05) inset, '
    + '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
};

const SimpleDealHeader = ({ ctrl, tabNav, changelog }) => {
  const { offer, confirm, team } = ctrl;
  if (!offer) return null;

  const eventTitle = offer.event_name;
  const { label: stateLabel, modifier: stateModifier } = statePillFor(offer, confirm);
  const backHref = `/teams/${team.id}/offers`;

  const [editEventOpen, setEditEventOpen] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);

  const handleSaveEventDetails = ({ eventName, venueId, startTime, ageRestriction }) => {
    const params = {
      event_name: eventName,
      venue_id: venueId ? Number(venueId) : null,
      age_restriction: ageRestriction || null,
    };
    const dates = Array.isArray(startTime) ? startTime : (startTime ? [startTime] : []);
    if (dates.length > 0) {
      params.start_date = toCalendarDate(dates[0]);
      params.start_time = offsetDateForServer(dates[0], offer.venue_time_zone || null);
    } else {
      params.start_date = null;
      params.start_time = null;
    }
    setSavingEvent(true);
    // updateOffer resolves to the saved offer on success and to undefined on
    // failure (it surfaces the error via toast internally). Close only on
    // success so a failed save keeps the modal open with the entered values.
    Promise.resolve(ctrl.updateOffer(offer.id, params))
      .then((saved) => { if (saved) setEditEventOpen(false); })
      .finally(() => setSavingEvent(false));
  };

  return (
    <>
    <header className="simple-deal-header">
      <div className="simple-deal-header__top-row">
        <a href={backHref} className="simple-deal-header__back-link">
          ‹ Offers
        </a>
      </div>

      <div className="simple-deal-header__title-row">
        <div className="simple-deal-header__title-group">
          {eventTitle && (
            <h1 className="simple-deal-header__title">{eventTitle}</h1>
          )}
          {stateLabel && (
            <span
              className={`simple-deal-header__state-pill simple-deal-header__state-pill--${stateModifier}`}
            >
              {stateLabel}
            </span>
          )}
          <Tippy content="Edit event details" placement="top">
            <button
              type="button"
              className="simple-deal-header__edit-event"
              onClick={() => setEditEventOpen(true)}
              aria-label="Edit event details"
              data-testid="header-edit-event"
            >
              <i className="far fa-pen" aria-hidden="true" />
            </button>
          </Tippy>
          {offer.calendar_event_id && (
            <a
              href={`/teams/${team.id}/confirms/${offer.calendar_event_id}`}
              className="simple-deal-header__event-link"
              data-testid="header-go-to-event"
            >
              Go to Event Details <i className="fas fa-arrow-right" aria-hidden="true" />
            </a>
          )}
        </div>

        {changelog && (
          <div className="simple-deal-header__changelog">{changelog}</div>
        )}
      </div>

      <div className="simple-deal-header__meta-row">
        <SimpleHeaderPills confirm={confirm} offer={offer} />

        <div className="simple-deal-header__template-actions">
          <EventTemplateBar
            team={team}
            csrfToken={ctrl.csrfToken}
            budget={ctrl.budgetForComponents}
            eventTemplates={ctrl.eventTemplates}
            onApplyTemplate={ctrl.handleApplyEventTemplate}
            onTemplateCreated={ctrl.handleEventTemplateCreated}
            onTemplateSaved={ctrl.handleEventTemplateSaved}
            onBudgetPatched={ctrl.handleBudgetPatched}
          />

          {!offer.calendar_event_id && (
            <div className="simple-deal-header__link-event-wrap">
              <button
                type="button"
                className="btn btn-setlist-secondary btn-sm simple-deal-header__link-event-btn"
                onClick={() => ctrl.setLinkOfferModalOpen(true)}
                data-testid="header-link-event"
              >
                <i className="fas fa-link mr-1" aria-hidden="true" />
                Link to Calendar Event
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="simple-deal-header__meta-row">
        {tabNav && (
          <div className="simple-deal-header__tabs">{tabNav}</div>
        )}

        <div className="simple-deal-header__icon-actions">
          <Tippy content="Switch to full editor" placement="top">
            <a
              href={`/teams/${team.id}/offers/v3/${offer.id}/edit`}
              aria-label="Switch to full editor"
              style={iconBtnStyle}
              data-testid="header-switch-to-v3"
            >
              <i className="far fa-table-layout" aria-hidden="true" />
            </a>
          </Tippy>

          <Tippy content="Preview" placement="top">
            <button
              type="button"
              aria-label="Preview"
              onClick={ctrl.handlePreview}
              style={iconBtnStyle}
              data-testid="header-preview"
            >
              <i className="far fa-eye" aria-hidden="true" />
            </button>
          </Tippy>

          <Tippy content="Copy offer" placement="top">
            <button
              type="button"
              aria-label="Copy"
              onClick={ctrl.handleCopy}
              style={iconBtnStyle}
              data-testid="header-copy"
            >
              <i className="far fa-copy" aria-hidden="true" />
            </button>
          </Tippy>

          <Tippy content={ctrl.canCopyHolds ? 'Copy hold dates' : 'No holds to copy'} placement="top">
            <span style={{ display: 'inline-flex' }}>
              <button
                type="button"
                aria-label="Copy hold dates"
                onClick={ctrl.handleCopyHolds}
                disabled={!ctrl.canCopyHolds}
                style={ctrl.canCopyHolds ? iconBtnStyle : disabledIconBtnStyle}
                data-testid="header-copy-holds"
              >
                <i className="far fa-clipboard" aria-hidden="true" />
              </button>
            </span>
          </Tippy>

          <Tippy content="Download PDF" placement="top">
            <button
              type="button"
              aria-label="Download"
              onClick={ctrl.handleDownload}
              style={iconBtnStyle}
              data-testid="header-download"
            >
              <i className="far fa-arrow-to-bottom" aria-hidden="true" />
            </button>
          </Tippy>

          <Tippy content="Create hold" placement="top">
            <button
              type="button"
              aria-label="Create hold"
              onClick={() => ctrl.setHoldSheetOpen(true)}
              style={iconBtnStyle}
              data-testid="header-create-hold"
            >
              <i className="far fa-calendar-plus" aria-hidden="true" />
            </button>
          </Tippy>

          {canCreateConfirm(offer, ctrl.currentUser) && (
            <Tippy content="Create a Confirm" placement="top">
              <button
                type="button"
                aria-label="Create a Confirm"
                onClick={() => ctrl.setCreateConfirmOpen(true)}
                style={iconBtnStyle}
                data-testid="header-create-confirm"
              >
                <i className="far fa-calendar-check" aria-hidden="true" />
              </button>
            </Tippy>
          )}

          <Tippy content="Delete offer" placement="top">
            <button
              type="button"
              aria-label="Delete"
              onClick={ctrl.handleDelete}
              style={dangerIconBtnStyle}
              data-testid="header-delete"
            >
              <i className="far fa-trash" aria-hidden="true" />
            </button>
          </Tippy>

          <button
            type="button"
            className="btn btn-primary text-nowrap"
            style={primaryBtnStyle}
            onClick={ctrl.handleSendOffer}
            data-testid="header-send"
          >
            <i className="far fa-paper-plane mr-1" aria-hidden="true" />
            Send offer
          </button>
        </div>
      </div>
    </header>

    <SimpleEventDetailsModal
      isOpen={editEventOpen}
      offer={offer}
      team={team}
      isSaving={savingEvent}
      onClose={() => setEditEventOpen(false)}
      onSave={handleSaveEventDetails}
    />
    </>
  );
};

SimpleDealHeader.propTypes = {
  ctrl: PropTypes.shape({
    offer: PropTypes.object.isRequired,
    confirm: PropTypes.object,
    team: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired }).isRequired,
    setLinkOfferModalOpen: PropTypes.func.isRequired,
    handlePreview: PropTypes.func.isRequired,
    handleCopy: PropTypes.func.isRequired,
    handleDownload: PropTypes.func.isRequired,
    canCopyHolds: PropTypes.bool,
    handleCopyHolds: PropTypes.func,
    handleDelete: PropTypes.func.isRequired,
    handleSendOffer: PropTypes.func.isRequired,
  }).isRequired,
  tabNav: PropTypes.node,
  changelog: PropTypes.node,
};

SimpleDealHeader.defaultProps = {
  tabNav: null,
  changelog: null,
};

export default SimpleDealHeader;
